/**
 * Sandbox monitoring component displaying live stats.
 * Uses an exec session to collect system stats information, with API-based fallback
 * for containers that don't have bash/proc tools (e.g., distroless images).
 *
 * @module components/terminal/SandboxMonitor
 */

import { useMemo, useRef } from 'react';
import { useSandboxMonitor } from './useSandboxMonitor';
import type { CpuStats, StatsData } from './useSandboxMonitor';
import { useSandboxStats } from '@/hooks';
import { StatsPanel } from './StatsPanel';
import type { SandboxStatsResponse } from '@/types';

interface SandboxMonitorProps {
  sandboxId: string;
  /** Optional sandbox group ID — when set, uses group-scoped URL paths */
  sandboxGroupId?: string;
  compact?: boolean;
}

/** Format bytes into a human-readable string (e.g., "1.5G", "256M"). */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0B';
  const units = ['B', 'K', 'M', 'G', 'T'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val < 10 ? val.toFixed(1) : Math.round(val)}${units[i]}`;
}

/**
 * Converts API stats response to the StatsData format used by StatsPanel.
 * Used as a fallback when exec-based monitoring is unavailable.
 */
function apiStatsToStatsData(apiStats: SandboxStatsResponse): StatsData | null {
  // Validate that the API returned real data before trusting it over exec fallback.
  // On any running VM, CPU idle ticks accumulate immediately and memory total is always set.
  // If both are zero/missing, the Instance Agent likely doesn't support GetStats yet,
  // so we return null to keep the exec-based WebSocket monitor active.
  const hasCpu = apiStats.cpu && (apiStats.cpu.idle > 0 || apiStats.cpu.user > 0);
  const hasMem = apiStats.memory && apiStats.memory.totalBytes > 0;
  if (!hasCpu && !hasMem) {
    return null;
  }

  return {
    cpu: apiStats.cpu
      ? {
          user: apiStats.cpu.user,
          nice: apiStats.cpu.nice,
          system: apiStats.cpu.system,
          idle: apiStats.cpu.idle,
          iowait: apiStats.cpu.iowait,
          irq: apiStats.cpu.irq,
          softirq: apiStats.cpu.softirq,
          steal: apiStats.cpu.steal,
        }
      : { user: 0, nice: 0, system: 0, idle: 0, iowait: 0, irq: 0, softirq: 0, steal: 0 },
    memory: apiStats.memory
      ? {
          total_kb: Math.round(apiStats.memory.totalBytes / 1024),
          free_kb: Math.round(apiStats.memory.availableBytes / 1024),
          available_kb: Math.round(apiStats.memory.availableBytes / 1024),
        }
      : { total_kb: 0, free_kb: 0, available_kb: 0 },
    loadavg: apiStats.cpu
      ? {
          '1min': apiStats.cpu.loadAvg1,
          '5min': apiStats.cpu.loadAvg5,
          '15min': apiStats.cpu.loadAvg15,
        }
      : { '1min': 0, '5min': 0, '15min': 0 },
    uptime_secs: apiStats.uptimeSecs ?? 0,
    network: apiStats.network
      ? { rx_bytes: apiStats.network.rxBytes, tx_bytes: apiStats.network.txBytes }
      : { rx_bytes: 0, tx_bytes: 0 },
    disk: apiStats.disk?.length
      ? apiStats.disk.map((d) => ({
          mount: d.mountPoint,
          size: formatBytes(d.totalBytes),
          used: formatBytes(d.usedBytes),
          avail: formatBytes(d.availableBytes),
          percent: d.totalBytes > 0 ? `${Math.round((d.usedBytes / d.totalBytes) * 100)}%` : '0%',
          label: d.label, // Pass through the human-friendly label if available
        }))
      : [],
  };
}

/**
 * Sandbox monitor - renders the Stats panel using a shared exec session for data collection.
 * Falls back to API-based stats when exec-based monitoring is unavailable (e.g., distroless containers).
 * When compact=true, renders a thin horizontal stats bar instead of the full boxy panel.
 */
export function SandboxMonitor({ sandboxId, sandboxGroupId, compact }: SandboxMonitorProps) {
  // Fetch stats via API (polled every 2 seconds, matching exec stream interval)
  const { data: sandboxStats } = useSandboxStats(sandboxId, true, 2000, sandboxGroupId);

  // Track whether API stats are available — once available, disable exec WebSocket
  const apiAvailableRef = useRef(false);
  const apiStats = useMemo(() => {
    if (!sandboxStats) return null;
    const result = apiStatsToStatsData(sandboxStats);
    if (result) apiAvailableRef.current = true;
    return result;
  }, [sandboxStats]);

  // Exec-based fallback: only enabled when API stats are NOT available
  const execEnabled = !apiAvailableRef.current;
  const {
    connectionState,
    stats: execStats,
    prevCpu: execPrevCpu,
    networkRate: execNetworkRate,
    error,
    retry,
  } = useSandboxMonitor(sandboxId, execEnabled, sandboxGroupId);

  // Track previous API CPU stats for delta calculation
  const prevApiCpuRef = useRef<CpuStats | null>(null);
  const prevApiNetworkRef = useRef<{ rx_bytes: number; tx_bytes: number } | null>(null);
  const lastApiPollTimeRef = useRef<number>(0);

  // Update previous API CPU on each poll
  const apiPrevCpu = useMemo(() => {
    const prev = prevApiCpuRef.current;
    if (apiStats?.cpu) {
      prevApiCpuRef.current = apiStats.cpu;
    }
    return prev;
  }, [apiStats]);

  // Compute API network rate from deltas between polls
  const apiNetworkRate = useMemo(() => {
    if (!apiStats?.network) return { rx: 0, tx: 0 };
    const now = Date.now();
    const prev = prevApiNetworkRef.current;
    const elapsed = lastApiPollTimeRef.current > 0 ? (now - lastApiPollTimeRef.current) / 1000 : 0;
    prevApiNetworkRef.current = apiStats.network;
    lastApiPollTimeRef.current = now;
    if (prev && elapsed > 0) {
      return {
        rx: Math.max(0, (apiStats.network.rx_bytes - prev.rx_bytes) / elapsed),
        tx: Math.max(0, (apiStats.network.tx_bytes - prev.tx_bytes) / elapsed),
      };
    }
    return { rx: 0, tx: 0 };
  }, [apiStats]);

  // Use API stats first (works on distroless images), fall back to exec-based stats
  const useApiStats = apiStats !== null;
  const stats = useApiStats ? apiStats : execStats;
  const prevCpu = useApiStats ? apiPrevCpu : execPrevCpu;
  const networkRate = useApiStats ? apiNetworkRate : execNetworkRate;

  // When API stats are active, suppress exec connection state/errors
  const effectiveConnectionState = useApiStats ? 'connected' : connectionState;
  const effectiveError = useApiStats ? null : error;

  return (
    <div className={compact ? 'h-full' : undefined}>
      <StatsPanel
        connectionState={effectiveConnectionState}
        stats={stats}
        prevCpu={prevCpu}
        networkRate={networkRate}
        error={effectiveError}
        onRetry={retry}
        tokenUsage={sandboxStats?.tokenUsage}
        compact={compact}
      />
    </div>
  );
}
